import React, { type ReactNode } from "react";
import Image from "next/image";
import { TextTitle } from "ui/text";
import { CenteredLoopSlider } from "ui/slider";
import { Box, type SpaceProps, Text, Link } from "@chakra-ui/react";
import { horizontalContainerPadding } from "ui/container";
import { Section } from "components/section";
import { remCalc } from "theme/utils";
import Balancer from "react-wrap-balancer";
import { useSwiperSlide } from "swiper/react";

const awards: AwardCardProps[] = [
  {
    title: (
      <>
        Technology Partner
        <br />
        of the Year
      </>
    ),
    url: "https://www.prnewswire.com/news-releases/iterable-names-hightouch-as-technology-partner-of-the-year-and-announces-smart-ingest-302133519.html?tc=eml_cleartime",
    image: {
      src: getAwardImagePath("iterable.png"),
      alt: "Iterable logo.",
    },
  },
  {
    title: (
      <>
        Marketplace Partner
        <br />
        of the Year
      </>
    ),
    url: "https://www.prnewswire.com/news-releases/hightouch-named-snowflake-marketplace-partner-of-the-year-for-media-entertainment--advertising-301567629.html",
    image: {
      src: getAwardImagePath("snowflake.png"),
      alt: "Snowflake logo.",
    },
  },
  {
    title: (
      <>
        Reverse ETL
        <br />
        Category Leader
      </>
    ),
    url: "https://www.g2.com/categories/reverse-etl",
    image: {
      src: getAwardImagePath("g2-summer-2024-leader.png"),
      alt: "G2 Award: Reverse ETL Category Leader.",
    },
  },
  {
    title: "InfraRed 100 in Cloud Data Infrastructure",
    url: "https://www.prnewswire.com/news-releases/hightouch-named-to-redpoints-infrared-100-302175636.html",
    image: {
      src: getAwardImagePath("redpoint.png"),
      alt: "Redpoint logo.",
    },
  },
  {
    title: (
      <>
        Best Places
        <br />
        to Work
      </>
    ),
    url: "https://www.prnewswire.com/news-releases/built-in-honors-hightouch-in-its-esteemed-2023-best-places-to-work-awards-301719470.html",
    image: {
      src: getAwardImagePath("built-in-2023-best-places-to-work.png"),
      alt: "Built In Award: 2023 Best Places to Work.",
    },
  },
  {
    title: (
      <>
        Ecosystem Partner
        <br />
        of the Year
      </>
    ),
    url: "https://hightouch.com/blog/fivetran-ecosystem-partner-of-the-year",
    image: {
      src: getAwardImagePath("fivetran.png"),
      alt: "Fivetran logo.",
    },
  },
  {
    title: (
      <>
        Best Estimated
        <br />
        ROI
      </>
    ),
    url: "https://www.g2.com/products/hightouch/reviews",
    image: {
      src: getAwardImagePath("g2-summer-2024-best-est-roi.png"),
      alt: "G2 Award: Best Estimated ROI.",
    },
  },
  {
    title: "Cool Vendor in Marketing Data & Analytics",
    url: "https://hightouch.com/blog/gartner-cool-vendor",
    image: {
      src: getAwardImagePath("gartner.png"),
      alt: "Gartner logo.",
    },
  },
];

function getAwardImagePath(filename: string) {
  return `/assets/industry-leader-awards/${filename}`;
}

type IndustryLeadersProps = {
  /**
   * Optionally hide the background color and use margin for spacing instead of padding.
   * Used to support rendering this component on legacy pages that have not be redesigned.
   *
   * @todo remove this prop after all pages using this component are updated
   */
  hideBg?: boolean; // eslint-disable-line react/boolean-prop-naming
  title?: string;
} & SpaceProps;

export function IndustryLeaderAwards({
  title = "Recognized as an industry leader by industry leaders",
  hideBg,
  ...spaceProps
}: IndustryLeadersProps) {
  return (
    <Section fullWidth noContainerPx bg={hideBg ? undefined : "brand.backgroundSecondary.light"} {...spaceProps}>
      <TextTitle
        align="center"
        mobileAlign="center"
        size="lg"
        title={
          <Balancer ratio={0.1} preferNative={false}>
            {title}
          </Balancer>
        }
        mb={{ base: "biggest", md: "huge" }}
        px={horizontalContainerPadding}
      />

      <CenteredLoopSlider controlsMt="0">
        {awards.map((award) => (
          <AwardCard key={award.url} {...award} />
        ))}
      </CenteredLoopSlider>
    </Section>
  );
}

type AwardCardProps = {
  title: ReactNode;
  url: string;
  image: {
    src: string;
    alt: string;
  };
};

function AwardCard({ title, url, image }: AwardCardProps) {
  const { isDuplicate } = useSwiperSlide();

  return (
    <Box>
      <Link
        tabIndex={isDuplicate ? -1 : undefined}
        href={url}
        target="_blank"
        variant="lightShadowCard"
        w={remCalc(312)}
        h="100%"
      >
        <Box layerStyle="card.media" h={remCalc(240)}>
          <Image fill src={image.src} alt={image.alt} sizes="100%" style={{ objectFit: "cover" }} />
        </Box>

        <Text align="center" textStyle="body3" fontWeight="semibold">
          {title}
        </Text>
      </Link>
    </Box>
  );
}
